import { Button, Layout } from "antd"
import React, { Suspense, useEffect } from "react"
import { Menu as MenuIcon, X as CloseIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { useKYBData, useWallet } from "Shared/hooks/corporate.hooks"
import { useCard } from "Shared/hooks/creditCard.hooks"
import { RootState } from "Shared/store/rootState.interface"
import { isDev, isInternal } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import IdlCounterNavbar from "./idlCounter.navbar"
import Icon from "Components/icon"
import { useModal } from "Shared/context/modal.context"
import UpgradeModal from "Components/upgradeModal"
import { useIsFreeVersion, useTenantTier } from "Shared/hooks/featureFlags.hooks"
import { BetaTag } from "Components/tags/BetaTag"
import { useCompany } from "Shared/hooks/company.hooks"
import { AutoLogoutEnum } from "@finway-group/shared/lib/models/company/autoLogout.enum"

const ActionMenuNavbar = React.lazy(() => import("./actionMenu.navbar"))
const UserProfileNavbar = React.lazy(() => import("./user.navbar"))
const ActivityContainer = React.lazy(() => import("./activity/activity.container"))
const CompanySelectionNavBar = React.lazy(() => import("./companySelection.navbar"))

const { Header } = Layout

interface NavbarInterface {
    onMobileDevice: boolean
    showMobileNav: boolean
    onMobileToggleClick(visible: boolean): void
}

const Navbar: React.FC<NavbarInterface> = (props: NavbarInterface) => {
    const { t } = useTranslation()
    const { showModal } = useModal()
    const isFreeVersion = useIsFreeVersion()
    const tenantTier = useTenantTier()
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
    const [showMobileNav, setShowMobileNav] = useStateIfMounted(false)
    const company = useCompany()
    const idleCounterEnabled = [AutoLogoutEnum.FIFTEEN_MINUTES, AutoLogoutEnum.ONE_HOUR].includes(company?.autoLogout)

    const toggleMobileNav = (visible: boolean): void => {
        props.onMobileToggleClick(visible)
        setShowMobileNav(visible)
    }

    useEffect(() => {
        toggleMobileNav(props.showMobileNav)
    }, [props.showMobileNav])

    const corporateId = useKYBData()?.corporateId
    const accountId = useWallet()?.accountId
    const cardId = useCard()?.cardId

    const isDebugEnabled = !props.onMobileDevice && (isDev || isInternal)

    const getDebugInfo = () => {
        return `Debug Info: Corporate ID: ${corporateId || "n/a"} - Account ID: ${accountId || "n/a"} - Card ID: ${cardId || "n/a"} - Tier: ${tenantTier}`
    }

    return (
        <Header className={"bg-primary w-full fixed z-60 top-0 h-56 px-20"}>
            <div className="container">
                <div className="flex justify-between items-center h-56 py-10">
                    <div onClick={() => toggleMobileNav(!showMobileNav)} className={!props.onMobileDevice ? "hidden" : "" + " cursor"}>
                        {showMobileNav ? <CloseIcon color={"white"} /> : <MenuIcon color={"white"} />}
                    </div>

                    <div className="flex flex-row items-center">
                        <Link className="hidden md:block finway-logo" to="/">
                            <img src={"/images/logo.svg"} className="w-120 sm:w-140 mt-4" alt={"finway"} />
                        </Link>
                        {isFreeVersion && (
                            <div className="hidden md:block">
                                <BetaTag />
                            </div>
                        )}
                    </div>

                    {isDebugEnabled && <div style={{ color: "#ffffff", fontSize: "12px" }}>{getDebugInfo()}</div>}

                    <div className="flex flex-row items-center">
                        {isFreeVersion ? (
                            <Button className="mr-10 btn-upgrade-version" onClick={() => showModal(UpgradeModal, true, { isShowing: true })}>
                                <Icon icon="rocket" className="mr-8" />
                                {t("label:upgrade")}
                            </Button>
                        ) : (
                            <>
                                <Suspense fallback={<></>}>
                                    <CompanySelectionNavBar />
                                </Suspense>

                                <Suspense fallback={<></>}>
                                    <ActionMenuNavbar />
                                </Suspense>

                                <Suspense fallback={<></>}>
                                    <ActivityContainer />
                                </Suspense>
                            </>
                        )}
                        {isAuthenticated ? (
                            <Suspense fallback={<></>}>
                                <UserProfileNavbar />
                            </Suspense>
                        ) : (
                            <div className={"flex items-center"}>
                                <Link className="text-white" to="/login">
                                    {t("nav:login")}
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Header>
    )
}

export default Navbar
